/**
 * ConnectHero Component
 * Hero section with breadcrumb, heading, subtitle, personalization banner,
 * and community/ritual carousel
 */

import { ChevronRight, MapPin, MoreVertical, Users } from "lucide-react"
import { memo, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Carousel } from "@/components/carousel/Carousel"
import { formatLocation } from "@/components/shared/formatLocation"
import { PersonalizationBanner } from "@/components/shared/PersonalizationBanner"
import { ShareModal } from "@/components/shared/ShareModal"
import type { CarouselApi } from "@/components/ui/carousel"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTranslation } from "@/language"
import { cn } from "@/lib"

import type { ConnectCommunity } from "../types/connect.types"

interface ConnectHeroProps {
  communities: ConnectCommunity[]
}

export const ConnectHero = memo(function ConnectHero({ communities }: ConnectHeroProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const { t } = useTranslation("common")

  useEffect(() => {
    if (!api) return

    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  return (
    <section className="mb-7.5 md:mb-12.5">
      {/* Breadcrumb */}
      <div className="md:pt-6 max-w-360 mx-auto px-5 md:px-17.5">
        <nav
          className="text-[13px] text-text-primary font-monteserat mb-2 gap-1 hidden md:flex items-center"
          aria-label={t("connect.breadcrumbHome")}
        >
          <Link to="/" className="hover:opacity-80 transition-opacity">
            {t("connect.breadcrumbHome")}
          </Link>
          <ChevronRight size={17} />
          <span>{t("connect.breadcrumbConnect")}</span>
        </nav>

        {/* Heading */}
        <h1 className="text-xl font-semibold leading-normal md:section-heading md:text-[30px] text-text-primary/90 md:mt-9">
          {t("connect.title")}
        </h1>
        <p className="text-[14px] text-text-primary/90 mt-1.25 md:mt-3 mb-5">
          {t("connect.heroSubtitle")}
        </p>

        {/* Personalization Banner */}
        <PersonalizationBanner className="mb-7.5 mt-4.5 md:mt-7" />
      </div>

      {/* Community Carousel */}
      {communities.length > 0 ? (
        <div className="relative">
          <Carousel
            data={communities}
            renderItem={community => <HeroCarouselCard community={community} />}
            itemWidth="min(656px, 100%)"
            contentClassName="w-full"
            setApi={setApi}
          />
          {communities.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              {communities.map((_, index) => (
                <button
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  className={cn(
                    "h-1 w-7.5 rounded-none transition-all",
                    index === current
                      ? "bg-text-primary"
                      : "bg-text-primary/20 hover:bg-text-primary/40",
                  )}
                  aria-label={t("accessibility.goToSlide", { n: index + 1 })}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-[300px] md:h-[400px] rounded-tl-[60px] rounded-br-[60px] bg-card text-primary/60 max-w-360 mx-auto px-5 md:px-17.5">
          <p className="text-sm">{t("connect.noHighlightedCommunities")}</p>
        </div>
      )}
    </section>
  )
})

function HeroCarouselCard({ community }: { community: ConnectCommunity }) {
  const location = formatLocation(community.city, community.state)
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const communityUrl = `${window.location.origin}/communities/${community.slug || community.id}`
  const [shareModalOpen, setShareModalOpen] = useState(false)

  const handleCopyLink = () => {
    navigator.clipboard.writeText(communityUrl)
    toast.success(t("connect.linkCopied"))
  }

  const handleShare = () => {
    setShareModalOpen(true)
  }

  return (
    <>
      <Link
        to={`/communities/${community.slug || community.id}`}
        className="block relative aspect-[16/11] w-full overflow-hidden rounded-[20px] group"
      >
        {/* Cover image */}
        {community.coverImage ? (
          <img
            src={community.coverImage}
            alt={community.name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="absolute inset-0 bg-card" />
        )}

        {/* Top-right action button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
              }}
              className="absolute top-3 right-3 z-10 w-[44px] h-[44px] flex items-center justify-center rounded-[10px] cursor-pointer transition-colors shadow-[0_5px_10px_rgba(2,2,2,0.30)] border-gradient-glass"
              aria-label={t("accessibility.moreOptions")}
            >
              <MoreVertical className="w-5 h-5 text-text-primary" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
            <DropdownMenuItem onClick={handleCopyLink}>{t("connect.copyLink")}</DropdownMenuItem>
            <DropdownMenuItem onClick={handleShare}>{t("connect.share")}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={e => {
                e.preventDefault()
                navigate(`/communities/${community.slug || community.id}`)
              }}
            >
              {t("connect.viewCommunity")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Bottom gradient overlay with backdrop blur */}
        <div className="absolute bottom-0 left-0 right-0 h-[113px] bg-gradient-to-t from-[rgba(0,0,0,0.7)] to-[rgba(0,0,0,0)]]" />
        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-4">
          <h3 className="text-[14px] md:text-[18px] font-semibold text-text-primary opacity-90 mb-1.5 leading-tight">
            {community.name}
          </h3>
          <div className="flex items-center gap-[10px] text-[13px] text-text-primary opacity-80 mb-1">
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {community.memberCount} {t("connect.members")}
            </span>
            <span>
              {community.ritualCount} {t("connect.rituals")}
            </span>
          </div>
          {location && (
            <p className="flex items-center gap-1 text-[13px] text-text-primary opacity-60">
              <MapPin className="w-3.5 h-3.5" />
              {location}
            </p>
          )}
        </div>
      </Link>

      {/* Share Modal — rendered outside <Link> to prevent click-through navigation */}
      <ShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        shareUrl={`/communities/${community.slug || community.id}`}
        shareText={community.name}
        shareType="community"
        shareImageUrl={community.coverImage}
        displayPath={community.name}
      />
    </>
  )
}
